// frontend/src/Components/SyncTo/hooks/useSyncToApi.js
//
// Phase 32 — client wrapper over POST /api/sync-to/transcribe and
// POST /api/sync-to/apply. Returns { data, error } tuples (NOT throws).
//
// Retry policy (D-07 / D-07a / Phase 34):
//   - transcribe(): 3-attempt exponential backoff [2000, 5000, 12000] ms on
//     502 / network(0) / other-5xx. NEVER retry on 4xx (consent / flag-off /
//     over-limit — deterministic) or 504 (timeout — user decides via the
//     "Still processing" dialog; PITFALLS #7). error.attempts surfaces the
//     number of attempts used (1-3) for the FailureModal UX.
//   - apply(): 3-attempt exponential backoff [2000, 5000, 12000] ms on 502 /
//     504 / network errors only. NEVER retry on 4xx (consent / sanitize /
//     validation — fast-fail). Idempotency token generated ONCE per apply()
//     call and REUSED across retries so the server-side D-06 gate short-
//     circuits any duplicate delivery to exactly one append.
//
// Contract consumed by:
//   - Plan 04 frontend/src/Components/SyncTo/RecordingProvider.jsx (transcribe)
//   - Plan 04 frontend/src/Components/SyncTo/ReviewModal.jsx          (apply)
//
// Invariants:
//   - axios only (frontend/src/api.js:1 — 25+ existing axios call sites; zero
//     production fetch calls).
//   - No PHI logging — cleanedText / blob / transcript NEVER interpolated into
//     console.* (T-32-21). This file has ZERO console.* calls.
//   - idempotencyToken generated OUTSIDE the retry loop so the same token is
//     reused across attempts (T-32-16 / D-06).
//   - sessionStorage key 'user' matches existing project convention (verified
//     against api.js lines 663, 681, 699, 1577, 1597, 1819, 2104 and
//     SyncToButton.jsx / userIdFromJwt.js — all use 'user' not 'token').

import axios from 'axios';
import { useCallback, useMemo } from 'react';

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:3002';

const APPLY_BACKOFF_MS = [2000, 5000, 12000];

// Phase 34 — transcribe() backoff. Same schedule as /apply but separate constant
// so tuning remains per-endpoint. NEVER retry on 4xx (400/403/413 are
// deterministic client errors — consent, flag-off, over-75MB). DO retry on
// 502 (retry exhausted upstream — can still spike back up) and on network
// error (status 0). PITFALLS #7 — do NOT retry on 504 timeout; the user
// drives that decision via the "Still processing" dialog in RecordingProvider.
const TRANSCRIBE_BACKOFF_MS = [2000, 5000, 12000];

function getAuthHeader() {
  // Mirrors the existing frontend/src/api.js JWT convention — read from
  // sessionStorage. Key is 'user' per project-wide convention (api.js:663 et al
  // and SyncToButton.jsx sessionStorage.getItem('user')).
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('user') : null;
  return token ? { Authorization: 'Bearer ' + token } : {};
}

function sleepMs(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function generateIdempotencyToken() {
  // R9 — crypto.randomUUID is available in all Chromium/Firefox/Safari
  // versions the SyncTo button targets (the mime-picker gate). The fallback
  // is non-cryptographic but collision-resistant enough for session-scoped
  // idempotency (the server matches on strict ===, not security-sensitive).
  try {
    if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (_) { /* fall through */ }
  return String(Date.now()) + '-' + Math.random().toString(36).slice(2);
}

function pickAudioExtension(mimeType) {
  if (typeof mimeType === 'string') {
    if (mimeType.indexOf('webm') !== -1) return '.webm';
    if (mimeType.indexOf('ogg')  !== -1) return '.ogg';
    if (mimeType.indexOf('mp4')  !== -1) return '.mp4';
  }
  return '';
}

/**
 * useSyncToApi()
 *
 * Thin React hook over the SyncTo backend. Returns stable-reference callbacks.
 *
 * transcribe({ blob, mode, mimeType }) → Promise<{ data, error }>
 *   data:  { mode, transcript, phrases, durationMs, cleaned?, cleanupFailed? }
 *   error: { status: number, message: string } | null
 *   Retry: [2000, 5000, 12000]ms × 3 on 502/network(0)/other-5xx. NEVER on
 *          4xx (consent/flag-off/too-large — deterministic) or 504 (timeout;
 *          user decides via the "Still processing" dialog — PITFALLS #7).
 *          error.attempts = number of attempts used (1-3).
 *
 * apply({ sessionId, mode, cleanedText, transcript, durationMs }) → Promise<{ data, error }>
 *   // Note mode (Phase 32): body = { sessionId, mode:'note', cleanedText, transcript, durationMs, idempotencyToken }
 *   // Template mode (Phase 33): body = { sessionId, mode:'template', templateId, acceptedFields, transcript, durationMs, idempotencyToken }
 *   data:  { success: true, mode: 'note'|'template', applied: [...], idempotent?: true, templateId? }
 *   error: { status: number, message: string } | null
 *   Retry: [2000, 5000, 12000]ms × 3 on 502/504/network; NEVER on 4xx.
 *   Idempotency: hook generates one UUID per call; reuses across retries so
 *   server-side D-06 gate short-circuits any duplicate delivery.
 *
 * Phase 33 extension (33-04): apply() accepts an additional signature for
 * Template mode — callers pass templateId + acceptedFields instead of
 * cleanedText. The body builder branches on the explicit mode value and
 * ONLY forwards the fields the backend expects for that branch, so the
 * Note-mode contract is untouched (syncToRoutes.js /apply re-validates both
 * branches independently).
 *
 * @returns {{ transcribe: Function, apply: Function }}
 */
export function useSyncToApi() {
  const transcribe = useCallback(async ({ blob, mode, mimeType, templateId, locale } = {}) => {
    let lastErr = null;
    let attemptsUsed = 0;
    for (let i = 0; i < TRANSCRIBE_BACKOFF_MS.length; i++) {
      attemptsUsed = i + 1;
      try {
        const fd = new FormData();
        fd.append('audio', blob, 'audio' + pickAudioExtension(mimeType || (blob && blob.type)));
        fd.append('mode', mode);
        if (mode === 'template' && templateId) {
          fd.append('templateId', String(templateId));
        }
        // Per-recording language hint chosen in the consent modal — pins
        // Azure Fast Transcription's locale array (en/af) or forces LID (auto).
        // Omitted when not supplied so the backend default kicks in.
        if (locale === 'en' || locale === 'af' || locale === 'auto') {
          fd.append('locale', locale);
        }
        // axios sets multipart Content-Type with boundary automatically — do
        // NOT override it manually (would drop the boundary param).
        const resp = await axios.post(URL + '/api/sync-to/transcribe', fd, {
          headers: { ...getAuthHeader() },
          maxBodyLength:    100 * 1024 * 1024,
          maxContentLength: 100 * 1024 * 1024,
          // No timeout — server enforces 8-minute ceiling via AbortController
          // (Phase 31 Plan 02). Local timeout would fight the server's retry.
        });
        return { data: resp.data, error: null };
      } catch (err) {
        const status = (err && err.response && err.response.status) || 0;
        lastErr = {
          status,
          message: (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Network error',
          attempts: attemptsUsed,
        };
        // PITFALLS #7 + Phase 31 D-07 — NEVER retry on 4xx (deterministic) or 504 (user decides).
        if (status >= 400 && status < 500) {
          return { data: null, error: lastErr };
        }
        if (status === 504) {
          return { data: null, error: lastErr };
        }
        // Retriable (502/0/5xx other than 504) — sleep, then next attempt.
        if (i < TRANSCRIBE_BACKOFF_MS.length - 1) {
          await sleepMs(TRANSCRIBE_BACKOFF_MS[i]);
        }
      }
    }
    // All attempts exhausted — surface the last error with attempts=3.
    return { data: null, error: lastErr };
  }, []);

  const apply = useCallback(async ({
    sessionId,
    mode,
    // Note-mode body fields (Phase 32)
    cleanedText,
    // Template-mode body fields (Phase 33)
    templateId,
    acceptedFields,
    // Shared
    transcript,
    durationMs,
    // Stage 4 — link this Apply to a noteTranscriptionJobs row so backend
    // marks the job APPLIED (so it doesn't resurface on patient-page mount).
    jobId,
  } = {}) => {
    // T-32-16 / D-06 — generate ONCE outside the retry loop so retries reuse.
    const idempotencyToken = generateIdempotencyToken();
    // Build the body per mode — backend /apply re-validates both branches and
    // rejects extra fields via its own whitelist, but we keep the client body
    // minimal per mode to avoid confusing the audit log on mode introspection.
    const body = mode === 'template'
      ? {
          sessionId,
          mode: 'template',
          templateId,
          acceptedFields: acceptedFields && typeof acceptedFields === 'object' ? acceptedFields : {},
          idempotencyToken,
          transcript: typeof transcript === 'string' ? transcript : '',
          durationMs: typeof durationMs === 'number' ? durationMs : 0,
          ...(jobId ? { jobId: String(jobId) } : {}),
        }
      : {
          sessionId,
          mode: mode || 'note',
          cleanedText,
          idempotencyToken,
          transcript: typeof transcript === 'string' ? transcript : '',
          durationMs: typeof durationMs === 'number' ? durationMs : 0,
          ...(jobId ? { jobId: String(jobId) } : {}),
        };

    let lastErr = null;
    for (let i = 0; i < APPLY_BACKOFF_MS.length; i++) {
      try {
        const resp = await axios.post(URL + '/api/sync-to/apply', body, {
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        });
        return { data: resp.data, error: null };
      } catch (err) {
        const status = (err && err.response && err.response.status) || 0;
        lastErr = {
          status,
          message: (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Network error',
        };
        // D-07 — NEVER retry on 4xx. Consent / sanitize / validation errors
        // are deterministic; retry only burns time.
        if (status >= 400 && status < 500) {
          return { data: null, error: lastErr };
        }
        // Sleep only between attempts (not after the last attempt).
        if (i < APPLY_BACKOFF_MS.length - 1) {
          await sleepMs(APPLY_BACKOFF_MS[i]);
        }
      }
    }
    return { data: null, error: lastErr };
  }, []);

  /**
   * transcribeAsync({ blob, mode, mimeType, sessionId, templateId })
   *   POST /api/sync-to/transcribe-async — uploads audio to Azure Blob Storage,
   *   creates a noteTranscriptionJobs row, returns { jobId } immediately.
   *   Backend responds 202; the worker processes off-band and notifies via
   *   Socket.IO `syncto:job-complete` (handled in BootNav.jsx).
   *
   *   Single attempt — no retry. The async path's whole point is that the
   *   user isn't waiting; if the upload itself fails, surface fast.
   *
   * @returns Promise<{ data: { jobId, status } | null, error }>
   */
  const transcribeAsync = useCallback(async ({ blob, mode, mimeType, sessionId, templateId, locale } = {}) => {
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'audio' + pickAudioExtension(mimeType || (blob && blob.type)));
      fd.append('mode', mode);
      fd.append('sessionId', String(sessionId || ''));
      if (mode === 'template' && templateId) {
        fd.append('templateId', String(templateId));
      }
      // See transcribe() above — same per-recording language hint, persisted
      // on the noteTranscriptionJobs doc so the worker picks the matching locale.
      if (locale === 'en' || locale === 'af' || locale === 'auto') {
        fd.append('locale', locale);
      }
      const resp = await axios.post(URL + '/api/sync-to/transcribe-async', fd, {
        headers: { ...getAuthHeader() },
        maxBodyLength:    100 * 1024 * 1024,
        maxContentLength: 100 * 1024 * 1024,
      });
      return { data: resp.data, error: null };
    } catch (err) {
      const status = (err && err.response && err.response.status) || 0;
      return {
        data: null,
        error: {
          status,
          message: (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Network error',
        },
      };
    }
  }, []);

  /**
   * getJob(jobId) — GET /api/sync-to/transcription-jobs/:jobId.
   * Used as a polling fallback and as the post-event fetch when the socket
   * notification arrives (the socket payload only carries jobId — the result
   * is read from this endpoint).
   */
  const getJob = useCallback(async (jobId) => {
    try {
      const resp = await axios.get(URL + '/api/sync-to/transcription-jobs/' + encodeURIComponent(String(jobId)), {
        headers: { ...getAuthHeader() },
      });
      return { data: resp.data, error: null };
    } catch (err) {
      const status = (err && err.response && err.response.status) || 0;
      return {
        data: null,
        error: {
          status,
          message: (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Network error',
        },
      };
    }
  }, []);

  /**
   * listJobs({ patientId, sessionId }) — GET /api/sync-to/transcription-jobs?…
   * Returns active jobs (QUEUED/PROCESSING/COMPLETE) for the patient or session.
   * Patient pages call this on mount to recover transcripts that completed
   * while the user was elsewhere or logged out.
   */
  const listJobs = useCallback(async ({ patientId, sessionId } = {}) => {
    try {
      const params = new URLSearchParams();
      if (patientId) params.set('patientId', String(patientId));
      if (sessionId) params.set('sessionId', String(sessionId));
      const resp = await axios.get(URL + '/api/sync-to/transcription-jobs?' + params.toString(), {
        headers: { ...getAuthHeader() },
      });
      return { data: resp.data, error: null };
    } catch (err) {
      const status = (err && err.response && err.response.status) || 0;
      return {
        data: null,
        error: {
          status,
          message: (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Network error',
        },
      };
    }
  }, []);

  /**
   * dismissJob(jobId) — POST /api/sync-to/transcription-jobs/:jobId/dismiss.
   * Marks a COMPLETE job as DISMISSED so the patient-page recovery flow
   * stops re-surfacing it. Fire-and-forget — failures are non-fatal (the
   * job lingers but no real harm; the user can dismiss again later).
   */
  const dismissJob = useCallback(async (jobId) => {
    if (!jobId) return { data: null, error: { status: 400, message: 'jobId required' } };
    try {
      const resp = await axios.post(
        URL + '/api/sync-to/transcription-jobs/' + encodeURIComponent(String(jobId)) + '/dismiss',
        {},
        { headers: { 'Content-Type': 'application/json', ...getAuthHeader() } },
      );
      return { data: resp.data, error: null };
    } catch (err) {
      const status = (err && err.response && err.response.status) || 0;
      return {
        data: null,
        error: {
          status,
          message: (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Network error',
        },
      };
    }
  }, []);

  return useMemo(
    () => ({ transcribe, transcribeAsync, getJob, listJobs, dismissJob, apply }),
    [transcribe, transcribeAsync, getJob, listJobs, dismissJob, apply],
  );
}
